import React, { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";

import {
    Animation,
    Container,
    ContainerBody,
    ContainerEmpty,
    ContainerIlustration,
    ContainerInfo
} from "./style";
import { Flow } from "../../../interfaces/Flow";
import lottie from "lottie-web";
import api from "../../../services/api";
import AutomationLog from "../../../interfaces/AutomationLog";
import { useToast } from "../../../hooks/toast";
import { ContainerLoader } from "../style";
import LogAutomationItem from "./LogAutomationItem";
import { formatDateToDateRead } from "../../../utils/formatDateToDateRead";
import emptyAutomations from '../../../assets/empty-state/empty-automations.png';

interface RunHistoryProps {
    open: boolean;
    flow: Flow;
    automation_id?: number;
    days: number;
    refreshTrigger?: number;
}

const RunHistory: React.FC<RunHistoryProps> = ({ open, flow, automation_id, days, refreshTrigger = 0 }) => {

    const [loading, setLoading] = useState<boolean>();
    const lottieContainer = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState(true);
    const parentRef = useRef<HTMLDivElement | null>(null);

    const { addToast } = useToast();

    const [logAutomations, setLogAutomations] = useState<AutomationLog[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

    const toggleGroup = (key: string) => {
        if (expandedGroups.includes(key)) {
            setExpandedGroups(expandedGroups.filter((group) => group !== key));
        } else {
            setExpandedGroups([...expandedGroups, key]);
        }
    };

    const getAutomationsLog = useCallback(async (pageToLoad: number, resetHasMore?: boolean) => {
        setLoading(true);
        setPage(pageToLoad);

        if (resetHasMore) {
            setHasMore(true);
        }

        try {
            const response = await api.post(`/automation/p/run-history`, {
                flow_id: flow.id_flow,
                automation_id: automation_id,
                days: days,
                page: pageToLoad
            });

            if (response.data !== null && response.data.length > 0) {
                let resp: AutomationLog[] = response.data;

                //Fill the dateGroup and dateTimeReadable fields            
                resp.forEach((log) => {
                    log.dayGroup = formatDateToDateRead(new Date(log.dt_started));
                    log.dateTimeRedable = formatDateToDateRead(new Date(log.dt_started), true);
                });

                if (pageToLoad === 1) {
                    setLogAutomations(resp);
                } else {
                    setLogAutomations(prevLogs => [...prevLogs, ...resp]);
                }
            } else {
                if (pageToLoad === 1) {
                    setLogAutomations([]);
                } else {
                    setHasMore(false);
                }
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            addToast({
                type: "error",
                title: "Erro ao buscar histórico de execução.",
                description: "Ao tentar buscar o histórico de execução, ocorreu um erro."
            });
        }
    }, [addToast, automation_id, days, flow.id_flow]);

    const handleScroll = debounce(() => {
        if (parentRef.current && hasMore && !loading) {
            const { scrollTop, scrollHeight, clientHeight } = parentRef.current;

            if (scrollTop + clientHeight >= scrollHeight - 300) {
                getAutomationsLog(page + 1);
            }
        }
    }, 200);
    
    const initialRenderDone = useRef(false);

    // Handle data fetching based on dialog state and refresh trigger
    useEffect(() => {
        if (open) {
            if (!initialRenderDone.current || refreshTrigger > 0) {
                initialRenderDone.current = true;
                getAutomationsLog(1, true);
            }
        } else {
            // Reset when dialog closes
            initialRenderDone.current = false;
        }
    }, [open, refreshTrigger, getAutomationsLog]);
    
    // Initialize or cleanup lottie animation
    useEffect(() => {
        let animation: any;
        
        if (lottieContainer.current) {
            animation = lottie.loadAnimation({
                container: lottieContainer.current,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: require('../../../assets/lottie/loader.json')
            });
        }
        
        // Cleanup animation on unmount or when loading state changes
        return () => {
            if (animation) animation.destroy();
        };
    }, [loading]);

    // Loading indicator for pagination
    const LoadingIndicator = () => (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '20px',
            gap: '10px' 
        }}>
            <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #3498db',
                animation: 'spin 1s linear infinite',
                fontSize: '13px',
            }}/>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            Carregando mais registros...
        </div>
    );

    return (
        <Container>
            {/* Show empty state when there are no logs */}
            {!loading && logAutomations.length === 0 && (
                <ContainerEmpty>
                    <ContainerIlustration>
                        <img src={emptyAutomations} alt="Sem Automações" />
                    </ContainerIlustration>
                    <ContainerInfo>
                        <h3>Você ainda não possui logs de execução neste fluxo</h3>
                        <p>Durante a execução das automações, os registros detalhados do processo serão exibidos nesta área.</p>
                    </ContainerInfo>
                </ContainerEmpty>
            )}

            {loading && page === 1 ?
                <ContainerLoader>
                    <Animation className="lottieContainer" ref={lottieContainer} />
                </ContainerLoader> :
                <>
                    <ContainerBody
                        ref={parentRef}
                        onScroll={handleScroll}
                    >
                        {logAutomations !== undefined && logAutomations.length > 0 && logAutomations.map((log, index) => {

                            let isExpanded = expandedGroups.includes(String(log.id_automation_log));

                            return (
                                <LogAutomationItem
                                    key={index}
                                    logAutomation={log}
                                    isExpanded={isExpanded}
                                    isShowAutomationName={automation_id === undefined}
                                    onExpand={toggleGroup}
                                />
                            )
                        })}
                        
                        {/* Show loading indicator when fetching more pages */}
                        {loading && page > 1 && <LoadingIndicator />}
                    </ContainerBody>
                </>
            }
        </Container>
    );

}

export default RunHistory;